import { FC, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'preact/compat'
import { useTranslation } from 'next-i18next'
import cn from 'classnames'
import s from './Modal.module.scss'
import { useRouter } from 'next/router'
import { X as Cross } from 'react-feather'
import {
    disableBodyScroll,
    enableBodyScroll,
    clearAllBodyScrollLocks
} from 'body-scroll-lock'
import FocusTrap from '@components/alfakher/pylot-ui/src/FocusTrap/FocusTrap'
import Button from '@corratech/pylot-ui/src/Button'

interface Props {
    className?: string
    children?: any
    open?: boolean
    onClose: () => void
    onEnter?: () => void | null
    focusFirst?: boolean
    closeOutSideClick?: boolean
}

const Modal: FC<Props> = ({
    className,
    children,
    open,
    onClose,
    onEnter = null,
    focusFirst = true,
    closeOutSideClick = true
}) => {
    const ref = useRef() as React.MutableRefObject<HTMLDivElement>
    const { t } = useTranslation('common')
    const router = useRouter()

    const handleKey = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                return onClose()
            }
        },
        [onClose]
    )

    const handleClickOutside = useCallback(
        (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose()
            }
        },
        [onClose]
    )

    useEffect(() => {
        if (ref.current) {
            if (open) {
                disableBodyScroll(ref.current)
                window.addEventListener('keydown', handleKey)
                if (closeOutSideClick) {
                    document.addEventListener('mousedown', handleClickOutside)
                }
            } else {
                enableBodyScroll(ref.current)
            }
        }
        return () => {
            window.removeEventListener('keydown', handleKey)
            document.removeEventListener('mousedown', handleClickOutside)
            clearAllBodyScrollLocks()
        }
    }, [open, handleKey, closeOutSideClick, handleClickOutside])

    const ModalContent: React.FC = () =>
        open ? (
            <div className={cn(s.root, className)}>
                <div
                    className={cn(
                        s['modal'],
                        'modal-wrapper',
                        `store_${router.locale}`
                    )}
                    role="dialog"
                    ref={ref}
                >
                    <div className={cn(s['modal-content'], 'modal-content')}>
                        <Button
                            variant="link"
                            onClick={() => onClose()}
                            aria-label={t('Close panel')}
                            className={cn(s['close-panel'], 'close-panel')}
                        >
                            <Cross />
                        </Button>
                        <div className={cn(s['modal-body'], 'modal-body')}>
                            <FocusTrap focusFirst={focusFirst}>
                                {children}
                            </FocusTrap>
                        </div>
                    </div>
                </div>
            </div>
        ) : null

    return typeof document !== 'undefined'
        ? createPortal(<ModalContent />, document.getElementById('__next')!)
        : null
}

export default Modal
